import { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';

import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import { useLoadingAnimation } from '@/components/LoadingAnimation/useLoadingAnimation.hook';
import { BILLING_TYPES } from '@/config/statuses.config';

import {
  CalculateUpliftedContractLinesInputType,
  ContractsAndBillingQuerycalculateUpliftedContractLinesByBillingCyclesArgs, UpliftMethodEnum
} from '@/graphql/contracts/_generated_/contracts';
import {
  calculateContractLineByUpliftAmount
} from '@/graphql/contracts/_generated_/calculateContractLineByUpliftAmount.query';
import calculateContractLineByUpliftAmountQuery
  from '@/graphql/contracts/calculateContractLineByUpliftAmount.query.gql';

import { notificationService } from '@/services/notification/notification.client';
import { Action } from '../components/ContractLinesGrid';

export interface UpliftData_Updated_Contractline {
  upliftValue: number | null;
  billingType?: string;
  newBillingPeriodValue?: string | null,
  newAnnualBillingPeriodValue?: string | null;
  newPricePerUnit?: string;
  contractLineID: string;
  pricePerUnit: string;
  annualBillingPeriodValue: string;
  billingPeriodValue: string;
  nextUpliftDate?: Date | null;
  action?: Action;
}

export default function useCalculateContractLineUplift() {
  const { toggleLoadingAnimation } = useLoadingAnimation();
  const [upliftedContractLine, setUpliftedContractLine] = useState<UpliftData_Updated_Contractline>();

  const [_calculateContractLine, { loading }]
    = useLazyQuery<calculateContractLineByUpliftAmount, 
    ContractsAndBillingQuerycalculateUpliftedContractLinesByBillingCyclesArgs>(
      calculateContractLineByUpliftAmountQuery,
      {
        client: apolloClientContracts,
        onError: (err) => notificationService.open({
          title: 'Error',
          text: err.message,
          type: 'danger'
        }),
      },
    );

  const calculateContractLineUplift = (
    contractLineID: string, amount: number,
    upliftMethod: string, pricePerUnit: number,
    currentAnnualBillinPeriodValue: string, currentBillingPeriodValue: string,
    nextUpliftDate?:Date) => {
    if (upliftMethod) {
      const values: CalculateUpliftedContractLinesInputType = {
        amount: amount,
        upliftMethod: upliftMethod === BILLING_TYPES[0].formattedType ?
          UpliftMethodEnum.UNIT_COST : UpliftMethodEnum.PERCENTAGE,
        filter: { includeContractLineIds: [contractLineID], includeDirectDebits: true, 
          includeExemptFromUplift: true },
      };

      _calculateContractLine({
        variables: {
          input: {
            ...values
          }
        },
        onCompleted: (upliftValues) => {

          if (!upliftValues || upliftValues?.calculateUpliftedContractLinesByBillingCycles?.length === 0) return;

          const data: UpliftData_Updated_Contractline = {
            pricePerUnit: upliftValues.calculateUpliftedContractLinesByBillingCycles![0]?.unitPrice,
            newPricePerUnit: upliftValues.calculateUpliftedContractLinesByBillingCycles![0]?.unitPrice,
            billingPeriodValue: upliftValues.calculateUpliftedContractLinesByBillingCycles![0]?.billingPeriodValue!,
            annualBillingPeriodValue: 
              upliftValues.calculateUpliftedContractLinesByBillingCycles![0]?.annualBillingPeriodValue!,
            contractLineID: upliftValues.calculateUpliftedContractLinesByBillingCycles![0]?.contractLineID!,
            upliftValue: amount,
            billingType: upliftMethod,
            newAnnualBillingPeriodValue: upliftValues
              .calculateUpliftedContractLinesByBillingCycles![0]?.annualBillingPeriodValue!,
            newBillingPeriodValue: upliftValues.calculateUpliftedContractLinesByBillingCycles![0]?.billingPeriodValue!,
            nextUpliftDate:nextUpliftDate
          };
          setUpliftedContractLine(data);
        }
      });
    } else {
      const data: UpliftData_Updated_Contractline = {
        pricePerUnit: pricePerUnit.toString(),
        billingPeriodValue: currentBillingPeriodValue,
        annualBillingPeriodValue: currentAnnualBillinPeriodValue,
        contractLineID: contractLineID,
        upliftValue: amount,
        nextUpliftDate:nextUpliftDate
      };
      setUpliftedContractLine(data);
    }
  };

  useEffect(() => {
    toggleLoadingAnimation(loading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return {
    calculateContractLineUplift,
    isLoadingUpdateContractLines: loading,
    upliftedContractLine
  } as const;
}
